import { MAIN_CONTROLLER } from "@/utils/main_controller.js";
import { API_BASE_URL } from "@/config";
import { validateToken } from '@/utils/auth';
import { validate_id } from "@/utils/validations.js";
import SlideTabComponent from '@/components/SlideTab/SlideTabComponent.vue';
import CustomTable from '@/components/CustomTable/CustomTable.vue';
import * as XLSX from 'xlsx';
import { useToast } from '@/components/Toast/useToast.js';

export default {
  name: 'BatchProcessing',
  mixins: [MAIN_CONTROLLER],
  components: {
    SlideTabComponent,
    CustomTable
  },

  setup() {
    const { showError, showSuccess } = useToast();
    return { showError, showSuccess };
  },

  data() {
    return {
      // Tab Navigation
      current_tab: 'upload',
      tabs: [
        { label: 'Upload', value: 'upload' },
        { label: 'Processing', value: 'processing' }
      ],

      // Constants
      RECORD_LIMIT: 100,

      // Workflow Data
      workflow_id: null,
      current_workflow_name: '',
      batch_type: 'individual', // 'individual' or 'company'
      batch_capabilities: {
        zoho_reference_mapping_enabled: false,
        required_fields: []
      },

      // File Upload
      batch_file: null,
      is_dragging: false,
      detected_columns: [],
      raw_file_content: null,
      parsed_file_rows: [],

      // Column Mapping - Individual
      id_number_column: '',
      first_name_column: '',
      last_name_column: '',

      // Column Mapping - Company
      company_name_column: '',
      registration_number_column: '',

      // Optional Zoho Column Mapping
      zoho_reference_column: '',

      // Extracted Records
      extracted_records: [],
      has_header_row: true,

      // Processing State
      batch_processing: false,
      active_batch_job: null,
      batch_job_data: {},
      batch_records: [],
      polling_interval: null,
      fetching_status: false,
      last_updated: null,
      loading_batch_status: false,
      retrying_index: null,

      // Duplicate Detection Modal
      show_duplicates_modal: false
    };
  },

  computed: {
    requiredFields() {
      const configured = Array.isArray(this.batch_capabilities.required_fields)
        ? this.batch_capabilities.required_fields.filter(Boolean)
        : [];

      if (configured.length > 0) {
        return configured;
      }

      return this.batch_type === 'company'
        ? ['name', 'registration_number']
        : ['id_number', 'first_name', 'last_name'];
    },

    // Column definitions for preview table
    preview_columns() {
      const zohoColumn = this.batch_capabilities.zoho_reference_mapping_enabled && this.zoho_reference_column
        ? [{ key: 'zoho_record_id', label: 'Zoho Record ID' }]
        : [];

      if (this.batch_type === 'individual') {
        const columns = [];
        if (this.id_number_column) columns.push({ key: 'id_number', label: 'ID Number' });
        if (this.first_name_column) columns.push({ key: 'first_name', label: 'First Name' });
        if (this.last_name_column) columns.push({ key: 'last_name', label: 'Last Name' });
        return [
          ...columns,
          ...zohoColumn,
          { key: 'status', label: 'Status' }
        ];
      } else {
        const columns = [];
        if (this.company_name_column) columns.push({ key: 'name', label: 'Company Name' });
        if (this.registration_number_column) columns.push({ key: 'registration_number', label: 'Registration Number' });
        return [
          ...columns,
          ...zohoColumn,
          { key: 'status', label: 'Status' }
        ];
      }
    },

    // Column definitions for processing table
    processing_columns() {
      if (this.batch_type === 'individual') {
        return [
          { key: 'name', label: 'Name' },
          { key: 'id_number', label: 'ID Number' },
          { key: 'status', label: 'Status' },
          { key: 'current_service', label: 'Current Service' },
          { key: 'progress', label: 'Progress' },
          { key: 'error', label: 'Error' }
        ];
      } else {
        return [
          { key: 'company_name', label: 'Company' },
          { key: 'registration_number', label: 'Registration' },
          { key: 'status', label: 'Status' },
          { key: 'current_service', label: 'Current Service' },
          { key: 'progress', label: 'Progress' },
          { key: 'error', label: 'Error' }
        ];
      }
    },

    valid_records_count() {
      return this.extracted_records.filter(r => r._has_required_data && r._validation && r._validation.all_valid === true).length;
    },

    invalid_records_count() {
      return this.extracted_records.filter(r => r._has_any_data && r._validation && r._validation.all_valid === false).length;
    },

    // Detect duplicate records
    duplicate_records() {
      if (this.batch_type === 'individual') {
        if (!this.isFieldRequired('id_number')) {
          return [];
        }

        const id_counts = {};
        const duplicates = [];
        
        this.extracted_records.forEach((record, index) => {
          const id = (record.id_number || '').trim();
          if (id) {
            if (id_counts[id] === undefined) {
              id_counts[id] = { count: 1, indices: [index] };
            } else {
              id_counts[id].count++;
              id_counts[id].indices.push(index);
            }
          }
        });
        
        Object.keys(id_counts).forEach(id => {
          if (id_counts[id].count > 1) {
            id_counts[id].indices.forEach(idx => {
              const record = this.extracted_records[idx];
              duplicates.push({
                row_number: idx + 1,
                identifier: id,
                name: `${record.first_name || ''} ${record.last_name || ''}`.trim(),
                occurrence_count: id_counts[id].count
              });
            });
          }
        });
        
        return duplicates;
      } else {
        if (!this.isFieldRequired('registration_number')) {
          return [];
        }

        const reg_counts = {};
        const duplicates = [];
        
        this.extracted_records.forEach((record, index) => {
          // Normalize: trim, uppercase, remove spaces/dashes/slashes for comparison
          const raw_reg = (record.registration_number || '').trim();
          const normalized_reg = raw_reg.toUpperCase().replace(/[\s\-/]/g, '');
          if (normalized_reg) {
            if (reg_counts[normalized_reg] === undefined) {
              reg_counts[normalized_reg] = { count: 1, indices: [index], original: raw_reg };
            } else {
              reg_counts[normalized_reg].count++;
              reg_counts[normalized_reg].indices.push(index);
            }
          }
        });
        
        Object.keys(reg_counts).forEach(normalized_reg => {
          if (reg_counts[normalized_reg].count > 1) {
            reg_counts[normalized_reg].indices.forEach(idx => {
              const record = this.extracted_records[idx];
              duplicates.push({
                row_number: idx + 1,
                identifier: record.registration_number || reg_counts[normalized_reg].original,
                name: record.name || '',
                occurrence_count: reg_counts[normalized_reg].count
              });
            });
          }
        });
        
        return duplicates;
      }
    },

    has_duplicates() {
      return this.duplicate_records.length > 0;
    },

    unique_duplicate_identifiers() {
      const seen = new Set();
      return this.duplicate_records.filter(d => {
        if (seen.has(d.identifier)) return false;
        seen.add(d.identifier);
        return true;
      });
    },

    // Column definitions for duplicates table in modal
    duplicate_columns() {
      if (this.batch_type === 'individual') {
        return [
          { key: 'row_number', label: 'Rows' },
          { key: 'identifier', label: 'ID Number' },
          { key: 'name', label: 'Name' },
          { key: 'occurrence_count', label: 'Occurrences' }
        ];
      } else {
        return [
          { key: 'row_number', label: 'Rows' },
          { key: 'identifier', label: 'Registration Number' },
          { key: 'name', label: 'Company Name' },
          { key: 'occurrence_count', label: 'Occurrences' }
        ];
      }
    },

    can_start_processing() {
      return this.batch_file &&
             this.requiredFields.every(fieldName => this.isFieldMapped(fieldName)) &&
             this.valid_records_count > 0 &&
             this.extracted_records.length <= this.RECORD_LIMIT &&
             !this.has_duplicates;
    },

    batch_status() {
      return this.batch_job_data.status || 'pending';
    },

    batch_status_text() {
      const statusMap = {
        'pending': 'Pending',
        'processing': 'Processing',
        'completed': 'Completed',
        'failed': 'Failed'
      };
      return statusMap[this.batch_status] || this.batch_status;
    },

    batch_status_icon() {
      const iconMap = {
        'pending': 'fas fa-clock',
        'processing': 'fas fa-spinner animate-spin',
        'completed': 'fas fa-check-circle',
        'failed': 'fas fa-exclamation-circle'
      };
      return iconMap[this.batch_status] || 'fas fa-clock';
    },

    total_records() {
      return this.batch_type === 'individual' 
        ? (this.batch_job_data.total_individuals || 0)
        : (this.batch_job_data.total_companies || 0);
    },

    processing_count() {
      return this.batch_records.filter(r => 
        String(r.status || '').toUpperCase() === 'PROCESSING'
      ).length;
    },

    successful_count() {
      return this.batch_type === 'individual'
        ? (this.batch_job_data.successful_individuals || 0)
        : (this.batch_job_data.successful_companies || 0);
    },

    failed_count() {
      return this.batch_type === 'individual'
        ? (this.batch_job_data.failed_individuals || 0)
        : (this.batch_job_data.failed_companies || 0);
    },

    retryable_count() {
      return this.batch_records.filter(r => r.status === 'failed').length;
    },

    overall_progress() {
      if (this.total_records === 0) return 0;
      const processed = this.batch_type === 'individual' 
        ? (this.batch_job_data.processed_individuals || 0)
        : (this.batch_job_data.processed_companies || 0);
      return Math.round((processed / this.total_records) * 100);
    }
  },

  methods: {
    // ==================== CustomTable Data Methods ====================
    fetch_preview_data(page, search) {
      // Return local extracted_records data in CustomTable format
      let filtered = [...this.extracted_records];
      
      // Apply search filter if provided
      if (search && search.trim()) {
        const searchLower = search.toLowerCase();
        if (this.batch_type === 'individual') {
          filtered = filtered.filter(r => 
            (r.id_number || '').toLowerCase().includes(searchLower) ||
            (r.first_name || '').toLowerCase().includes(searchLower) ||
            (r.last_name || '').toLowerCase().includes(searchLower)
          );
        } else {
          filtered = filtered.filter(r => 
            (r.name || '').toLowerCase().includes(searchLower) ||
            (r.registration_number || '').toLowerCase().includes(searchLower)
          );
        }
      }
      
      return {
        results: filtered,
        count: filtered.length
      };
    },

    fetch_processing_data(page, search) {
      // Return local batch_records data in CustomTable format
      let filtered = [...this.batch_records];
      
      // Apply search filter if provided
      if (search && search.trim()) {
        const searchLower = search.toLowerCase();
        if (this.batch_type === 'individual') {
          filtered = filtered.filter(r => 
            (r.id_number || '').toLowerCase().includes(searchLower) ||
            (r.first_name || '').toLowerCase().includes(searchLower) ||
            (r.last_name || '').toLowerCase().includes(searchLower)
          );
        } else {
          filtered = filtered.filter(r => 
            (r.company_name || '').toLowerCase().includes(searchLower) ||
            (r.registration_number || '').toLowerCase().includes(searchLower)
          );
        }
      }
      
      return {
        results: filtered,
        count: filtered.length
      };
    },

    // ==================== Workflow Methods ====================
    async fetch_workflow_details() {
      const TOKEN = localStorage.getItem('token');
      const API_URL = `${API_BASE_URL}/verify_api/workflow_management_api/get_workflow_details/${this.workflow_id}/`;

      try {
        const response = await fetch(API_URL, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${TOKEN}`
          }
        });

        const data = await response.json();
        
        if (data.status === 'success' && data.data) {
          this.current_workflow_name = data.data.workflow_name;
          this.batch_type = data.data.batch_onboarding_target === 'company' ? 'company' : 'individual';
          await this.fetch_batch_capabilities();
        } else if (data.workflow_name) {
          this.current_workflow_name = data.workflow_name;
          this.batch_type = data.batch_onboarding_target === 'company' ? 'company' : 'individual';
          await this.fetch_batch_capabilities();
        } else {
          this.show_error('Failed to load workflow details');
        }
      } catch (error) {
        console.error('Error fetching workflow details:', error);
        this.show_error('Failed to load workflow details');
      }
    },

    async fetch_batch_capabilities() {
      if (!this.workflow_id) {
        return;
      }

      const TOKEN = localStorage.getItem('token');
      const API_URL = `${API_BASE_URL}/verify_api/batch_api/batch_upload_capabilities/${this.workflow_id}/`;

      try {
        const response = await fetch(API_URL, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${TOKEN}`
          }
        });

        const data = await response.json();

        if (response.ok && data.status === 'success') {
          this.batch_capabilities = {
            zoho_reference_mapping_enabled: !!data.zoho_reference_mapping_enabled,
            required_fields: Array.isArray(data.required_fields) ? data.required_fields : []
          };

          if (this.detected_columns.length > 0) {
            this.auto_detect_columns();
            this.extract_records();
          }
        }
      } catch (error) {
        console.error('Error fetching batch capabilities:', error);
      }
    },

    // ==================== File Handling Methods ====================
    handle_file_upload(event) {
      const file = event.target.files[0];
      if (file) {
        this.process_file(file);
      }
    },

    handle_drop(event) {
      this.is_dragging = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.process_file(file);
      }
    },

    process_file(file) {
      // Validate file type
      const allowed_types = ['.csv', '.xlsx', '.xls'];
      const file_extension = '.' + file.name.split('.').pop().toLowerCase();

      if (!allowed_types.includes(file_extension)) {
        this.show_error('Please select a valid file format (CSV or Excel)');
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        this.show_error('File size must be less than 10MB');
        return;
      }

      this.batch_file = file;
      this.detect_columns();
    },

    remove_file() {
      this.batch_file = null;
      this.detected_columns = [];
      this.extracted_records = [];
      this.raw_file_content = null;
      this.parsed_file_rows = [];
      this.reset_column_mappings();
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    reset_column_mappings() {
      this.id_number_column = '';
      this.first_name_column = '';
      this.last_name_column = '';
      this.company_name_column = '';
      this.registration_number_column = '';
      this.zoho_reference_column = '';
    },

    // ==================== Column Detection Methods ====================
    detect_columns() {
      if (!this.batch_file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const file_extension = '.' + this.batch_file.name.split('.').pop().toLowerCase();

        try {
          if (file_extension === '.csv') {
            this.raw_file_content = e.target.result;
            this.parse_csv_columns(e.target.result);
          } else if (file_extension === '.xlsx' || file_extension === '.xls') {
            this.parse_excel_file(e.target.result);
          }
        } catch (error) {
          console.error('File parsing error:', error);
          this.show_error('Error reading file. Please check the file format.');
        }
      };

      if (this.batch_file.name.endsWith('.xlsx') || this.batch_file.name.endsWith('.xls')) {
        reader.readAsArrayBuffer(this.batch_file);
      } else {
        reader.readAsText(this.batch_file);
      }
    },

    isFieldRequired(fieldName) {
      return this.requiredFields.includes(fieldName);
    },

    isFieldMapped(fieldName) {
      const fieldMap = {
        id_number: this.id_number_column,
        first_name: this.first_name_column,
        last_name: this.last_name_column,
        name: this.company_name_column,
        registration_number: this.registration_number_column
      };

      return !this.isFieldRequired(fieldName) || !!fieldMap[fieldName];
    },

    getFieldLabel(fieldName, label) {
      return this.isFieldRequired(fieldName) ? label : `${label} (Optional)`;
    },

    parse_csv_columns(content) {
      const lines = content.split('\n').filter(line => line.trim());
      if (lines.length === 0) return;

      // Try different delimiters
      const delimiters = [',', ';', '\t', '|'];
      let best_delimiter = ',';
      let max_columns = 0;

      for (const delimiter of delimiters) {
        const columns = lines[0].split(delimiter).length;
        if (columns > max_columns) {
          max_columns = columns;
          best_delimiter = delimiter;
        }
      }

      const first_line = lines[0].split(best_delimiter);
      this.detected_columns = first_line.map(col => col.trim().replace(/['"]/g, ''));
      this.parsed_file_rows = lines.map(line => line.split(best_delimiter).map(col => col.trim().replace(/['"]/g, '')));
      
      this.auto_detect_columns();
      this.extract_records();
    },

    parse_excel_file(arrayBuffer) {
      try {
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const first_sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json_data = XLSX.utils.sheet_to_json(first_sheet, { header: 1 });

        if (json_data.length > 0) {
          this.parsed_file_rows = json_data.map(row => row.map(col => String(col || '').trim()));
          this.detected_columns = this.parsed_file_rows[0] || [];
          this.raw_file_content = this.parsed_file_rows.map(row => row.join(',')).join('\n');
          
          this.auto_detect_columns();
          this.extract_records();
        }
      } catch (error) {
        console.error('Excel parsing error:', error);
        this.show_error('Error reading Excel file');
      }
    },

    auto_detect_columns() {
      if (this.batch_type === 'individual') {
        const id_keywords = ['id', 'identity', 'national', 'passport', 'number'];
        const first_name_keywords = ['first', 'firstname', 'given', 'name'];
        const last_name_keywords = ['last', 'lastname', 'surname', 'family'];

        for (const col of this.detected_columns) {
          const lower = col.toLowerCase();
          
          if (this.isFieldRequired('id_number') && !this.id_number_column && id_keywords.some(k => lower.includes(k))) {
            this.id_number_column = col;
          }
          if (
            this.isFieldRequired('first_name') &&
            !this.first_name_column &&
            first_name_keywords.some(k => lower.includes(k)) &&
            !lower.includes('last')
          ) {
            this.first_name_column = col;
          }
          if (this.isFieldRequired('last_name') && !this.last_name_column && last_name_keywords.some(k => lower.includes(k))) {
            this.last_name_column = col;
          }
        }
      } else {
        const company_keywords = ['company', 'name', 'business', 'organization'];
        const reg_keywords = ['registration', 'reg', 'number', 'id', 'code'];

        for (const col of this.detected_columns) {
          const lower = col.toLowerCase();
          
          if (this.isFieldRequired('name') && !this.company_name_column && company_keywords.some(k => lower.includes(k))) {
            this.company_name_column = col;
          }
          if (
            this.isFieldRequired('registration_number') &&
            !this.registration_number_column &&
            reg_keywords.some(k => lower.includes(k))
          ) {
            this.registration_number_column = col;
          }
        }
      }
    },

    // ==================== Record Extraction Methods ====================
    extract_records() {
      const rows = Array.isArray(this.parsed_file_rows) && this.parsed_file_rows.length > 0
        ? this.parsed_file_rows
        : [];

      if (rows.length === 0) return;

      const headers = rows[0].map(col => String(col || '').trim().replace(/['"]/g, ''));
      const start_line = this.has_header_row ? 1 : 0;
      this.extracted_records = [];

      if (this.batch_type === 'individual') {
        const id_idx = this.id_number_column ? headers.indexOf(this.id_number_column) : -1;
        const first_idx = this.first_name_column ? headers.indexOf(this.first_name_column) : -1;
        const last_idx = this.last_name_column ? headers.indexOf(this.last_name_column) : -1;
        const zoho_idx = this.zoho_reference_column ? headers.indexOf(this.zoho_reference_column) : -1;

        for (let i = start_line; i < rows.length; i++) {
          const row = rows[i].map(col => String(col || '').trim().replace(/['"]/g, ''));
          
          const id_number = id_idx >= 0 ? (row[id_idx] || '') : '';
          const first_name = first_idx >= 0 ? (row[first_idx] || '') : '';
          const last_name = last_idx >= 0 ? (row[last_idx] || '') : '';
          const zoho_record_id = zoho_idx >= 0 ? (row[zoho_idx] || '') : '';
          const has_any_data = [id_number, first_name, last_name].some(value => (value || '').trim());

          if (has_any_data) {
            const validation = this.validate_individual(id_number, first_name, last_name);
            this.extracted_records.push({
              id_number,
              first_name,
              last_name,
              zoho_record_id,
              _has_any_data: has_any_data,
              _has_required_data: this.requiredFields.every(fieldName => {
                const fieldValues = { id_number, first_name, last_name };
                return (fieldValues[fieldName] || '').trim();
              }),
              _validation: validation
            });
          }
        }
      } else {
        const name_idx = this.company_name_column ? headers.indexOf(this.company_name_column) : -1;
        const reg_idx = this.registration_number_column ? headers.indexOf(this.registration_number_column) : -1;
        const zoho_idx = this.zoho_reference_column ? headers.indexOf(this.zoho_reference_column) : -1;

        for (let i = start_line; i < rows.length; i++) {
          const row = rows[i].map(col => String(col || '').trim().replace(/['"]/g, ''));
          
          const name = name_idx >= 0 ? (row[name_idx] || '') : '';
          const registration_number = reg_idx >= 0 ? (row[reg_idx] || '') : '';
          const zoho_record_id = zoho_idx >= 0 ? (row[zoho_idx] || '') : '';
          const has_any_data = [name, registration_number].some(value => (value || '').trim());

          if (has_any_data) {
            const validation = this.validate_company(name, registration_number);
            this.extracted_records.push({
              name,
              registration_number,
              zoho_record_id,
              _has_any_data: has_any_data,
              _has_required_data: this.requiredFields.every(fieldName => {
                const fieldValues = { name, registration_number };
                return (fieldValues[fieldName] || '').trim();
              }),
              _validation: validation
            });
          }
        }
      }

      // Reload preview table to reflect new data
      this.$nextTick(() => {
        if (this.$refs.previewTable) {
          this.$refs.previewTable.reload();
        }
      });
    },

    validate_individual(id_number, first_name, last_name) {
      const idRequired = this.isFieldRequired('id_number');
      const firstNameRequired = this.isFieldRequired('first_name');
      const lastNameRequired = this.isFieldRequired('last_name');
      const result = {
        id_valid: !idRequired,
        first_name_valid: !firstNameRequired,
        last_name_valid: !lastNameRequired,
        all_valid: false,
        id_error: null
      };

      if (idRequired && id_number && id_number.trim()) {
        const clean_id = id_number.toString().trim().replace(/\s/g, '');
        
        // Check basic format first (13 digits for SA ID)
        if (!/^\d{13}$/.test(clean_id)) {
          result.id_valid = false;
          result.id_error = 'ID must be 13 digits';
        } else {
          // Use the validate_id utility (returns boolean)
          const is_valid = validate_id(clean_id);
          result.id_valid = is_valid === true;
          if (!result.id_valid) {
            result.id_error = 'Invalid SA ID number (checksum failed)';
          }
        }
      } else if (idRequired) {
        result.id_error = 'ID number is required';
      }

      if (firstNameRequired) {
        result.first_name_valid = !!(first_name && first_name.trim().length >= 2);
      }

      if (lastNameRequired) {
        result.last_name_valid = !!(last_name && last_name.trim().length >= 2);
      }

      result.all_valid = result.id_valid && result.first_name_valid && result.last_name_valid;

      return result;
    },

    validate_company(name, registration_number) {
      const nameRequired = this.isFieldRequired('name');
      const registrationRequired = this.isFieldRequired('registration_number');
      const result = {
        name_valid: !nameRequired,
        registration_valid: !registrationRequired,
        validation_error: null,
        all_valid: false
      };

      if (nameRequired) {
        result.name_valid = !!(name && name.trim());
        if (!result.name_valid) {
          result.validation_error = 'Company name is required';
        }
      }

      if (registrationRequired && registration_number && registration_number.trim()) {
        const reg_trimmed = registration_number.trim();
        const reg_pattern = /^\d{4}\/\d{6}\/\d{2}$/;
        
        if (reg_pattern.test(reg_trimmed)) {
          // Extract year and validate it's not in the future
          const year = parseInt(reg_trimmed.substring(0, 4), 10);
          const current_year = new Date().getFullYear();
          if (year > current_year) {
            result.validation_error = 'Year cannot be in the future';
          } else {
            result.registration_valid = true;
          }
        } else {
          result.validation_error = 'Format must be yyyy/nnnnnn/nn';
        }
      } else if (registrationRequired && !result.validation_error) {
        result.validation_error = 'Registration number is required';
      }

      result.all_valid = result.name_valid && result.registration_valid;

      return result;
    },

    // ==================== Batch Processing Methods ====================
    async start_batch_processing() {
      if (!this.can_start_processing) return;

      this.batch_processing = true;
      const TOKEN = localStorage.getItem('token');
      
      // Filter valid records only
      let valid_records;
      let api_url;
      let payload;

      if (this.batch_type === 'individual') {
        valid_records = this.extracted_records.filter(r => r._validation && r._validation.all_valid);
        api_url = `${API_BASE_URL}/verify_api/batch_api/batch_upload_individuals/`;
        payload = {
          workflow_id: this.workflow_id,
          individuals: valid_records.map(r => ({
            ...(r.id_number ? { id_number: r.id_number } : {}),
            ...(r.first_name ? { first_name: r.first_name } : {}),
            ...(r.last_name ? { last_name: r.last_name } : {}),
            ...(r.zoho_record_id ? { zoho_record_id: r.zoho_record_id } : {})
          })),
          total_count: valid_records.length
        };
      } else {
        valid_records = this.extracted_records.filter(r => r._validation && r._validation.all_valid);
        api_url = `${API_BASE_URL}/verify_api/batch_api/batch_upload_companies/`;
        payload = {
          workflow_id: this.workflow_id,
          companies: valid_records.map(r => ({
            ...(r.name ? { name: r.name } : {}),
            ...(r.registration_number ? { registration_number: r.registration_number } : {}),
            ...(r.zoho_record_id ? { zoho_record_id: r.zoho_record_id } : {})
          })),
          total_count: valid_records.length
        };
      }

      try {
        const response = await fetch(api_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${TOKEN}`
          },
          body: JSON.stringify(payload)
        });

        const result = await response.json();

        if (response.ok && result.status === 'success') {
          this.active_batch_job = result.batch_job_id;
          this.batch_job_data = result;
          this.show_success('Batch processing started successfully');
          this.current_tab = 'processing';
          this.start_polling();
        } else {
          this.show_error(result.message || 'Failed to start batch processing');
        }
      } catch (error) {
        console.error('Batch processing error:', error);
        this.show_error('An error occurred while starting batch processing');
      } finally {
        this.batch_processing = false;
      }
    },

    start_polling() {
      this.stop_polling();
      this.fetch_batch_status();
      this.polling_interval = setInterval(() => {
        this.fetch_batch_status();
      }, 3000);
    },

    stop_polling() {
      if (this.polling_interval) {
        clearInterval(this.polling_interval);
        this.polling_interval = null;
      }
    },

    async fetch_batch_status() {
      if (!this.active_batch_job || this.fetching_status) return;

      this.fetching_status = true;
      const TOKEN = localStorage.getItem('token');
      const API_URL = `${API_BASE_URL}/verify_api/batch_api/batch_job_status/${this.active_batch_job}/`;

      try {
        const response = await fetch(API_URL, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${TOKEN}`
          }
        });

        const result = await response.json();

        if (response.ok) {
          this.batch_job_data = result;
          this.batch_records = this.batch_type === 'individual' 
            ? (result.individuals || [])
            : (result.companies || []);
          this.last_updated = new Date().toLocaleTimeString();

          // Reload processing table to reflect new data
          this.$nextTick(() => {
            if (this.$refs.processingTable) {
              this.$refs.processingTable.reload();
            }
          });

          // Stop polling if job is complete or failed
          if (result.status === 'completed' || result.status === 'failed') {
            this.stop_polling();
          }
        }
      } catch (error) {
        console.error('Error fetching batch status:', error);
      } finally {
        this.fetching_status = false;
      }
    },

    async retry_record(record, index) {
      if (!this.active_batch_job) return;

      this.retrying_index = index;
      const TOKEN = localStorage.getItem('token');
      
      const api_url = this.batch_type === 'individual'
        ? `${API_BASE_URL}/verify_api/batch_api/batch_individual_retry/`
        : `${API_BASE_URL}/verify_api/batch_api/batch_company_retry/`;

      const payload = this.batch_type === 'individual'
        ? {
            batch_job_id: this.active_batch_job,
            individual_progress_id: record.id,
            ...(record.id_number ? { id_number: record.id_number } : {}),
            ...(record.first_name ? { first_name: record.first_name } : {}),
            ...(record.last_name ? { last_name: record.last_name } : {})
          }
        : {
            batch_job_id: this.active_batch_job,
            company_progress_id: record.id,
            ...(record.company_name ? { company_name: record.company_name } : {}),
            ...(record.registration_number ? { registration_number: record.registration_number } : {})
          };

      try {
        const response = await fetch(api_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${TOKEN}`
          },
          body: JSON.stringify(payload)
        });

        const result = await response.json();

        if (response.ok && result.status === 'success') {
          this.show_success('Retry started successfully');
          // Update record status locally
          this.batch_records[index].status = 'processing';
          this.batch_records[index].error_message = null;
          // Restart polling
          this.start_polling();
        } else {
          this.show_error(result.message || 'Retry failed');
        }
      } catch (error) {
        console.error('Retry error:', error);
        this.show_error('An error occurred during retry');
      } finally {
        this.retrying_index = null;
      }
    },

    // ==================== Utility Methods ====================
    reset_upload() {
      this.batch_file = null;
      this.detected_columns = [];
      this.extracted_records = [];
      this.raw_file_content = null;
      this.parsed_file_rows = [];
      this.reset_column_mappings();
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    start_new_batch() {
      this.stop_polling();
      this.active_batch_job = null;
      this.batch_job_data = {};
      this.batch_records = [];
      this.reset_upload();
      this.current_tab = 'upload';
    },

    format_file_size(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    format_date(dateString) {
      if (!dateString) return '—';
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    },

    get_status_class(status) {
      const s = (status || '').toString().toLowerCase();
      const classMap = {
        'pending': 'pending',
        'processing': 'processing',
        'completed': 'completed',
        'failed': 'failed'
      };
      return classMap[s] || 'pending';
    },

    get_status_icon(status) {
      const s = (status || '').toString().toLowerCase();
      const iconMap = {
        'pending': 'fas fa-clock',
        'processing': 'fas fa-spinner animate-spin',
        'completed': 'fas fa-check',
        'failed': 'fas fa-times'
      };
      return iconMap[s] || 'fas fa-clock';
    },

    show_error(message) {
      this.showError('Error', message);
    },

    show_success(message) {
      this.showSuccess('Success', message);
    },

    open_duplicates_modal() {
      this.show_duplicates_modal = true;
      // Reload duplicates table when modal opens
      this.$nextTick(() => {
        if (this.$refs.duplicatesTable) {
          this.$refs.duplicatesTable.reload();
        }
      });
    },

    close_duplicates_modal() {
      this.show_duplicates_modal = false;
    },

    // Fetch data for duplicates CustomTable - returns unique identifiers only
    fetch_duplicates_data(page, search) {
      // Get unique duplicate identifiers with their row numbers combined
      const uniqueMap = {};
      this.duplicate_records.forEach(record => {
        if (!uniqueMap[record.identifier]) {
          uniqueMap[record.identifier] = {
            identifier: record.identifier,
            name: record.name,
            occurrence_count: record.occurrence_count,
            row_numbers: [record.row_number]
          };
        } else {
          uniqueMap[record.identifier].row_numbers.push(record.row_number);
          // Keep the first name found, or update if current is empty
          if (!uniqueMap[record.identifier].name && record.name) {
            uniqueMap[record.identifier].name = record.name;
          }
        }
      });

      let filtered = Object.values(uniqueMap).map(item => ({
        ...item,
        row_number: item.row_numbers.join(', ')
      }));
      
      // Apply search filter if provided
      if (search && search.trim()) {
        const searchLower = search.toLowerCase();
        filtered = filtered.filter(r => 
          (r.identifier || '').toLowerCase().includes(searchLower) ||
          (r.name || '').toLowerCase().includes(searchLower) ||
          (r.row_number || '').toLowerCase().includes(searchLower)
        );
      }
      
      return {
        results: filtered,
        count: filtered.length
      };
    }
  },

  mounted() {
    if (!validateToken(this.$router)) {
      return;
    }
    
    // Get workflow_id from route params
    this.workflow_id = this.$route.params.workflow_id;
    
    if (this.workflow_id) {
      this.fetch_workflow_details();
    } else {
      this.show_error('No workflow ID provided');
    }
  },

  beforeUnmount() {
    this.stop_polling();
  }
};
